import React, { useEffect, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Wifi, Copy, Eye, Crown, ArrowLeft, Check, MessageSquare,
} from "lucide-react";
import LocationPicker from "./LocationPicker";
import UserLink from "./UserLink";
import { T } from "../theme";

// ─── Design tokens (same as ScanKandiPage / UserDashboard) ───────────────────

const cardStyle = {
  width: "100%", maxWidth: 560,
  background: T.card,
  backdropFilter: "blur(24px)",
  WebkitBackdropFilter: "blur(24px)",
  border: T.border,
  borderRadius: T.radiusLg,
  padding: "36px 30px",
  boxShadow: "0 24px 64px rgba(0,0,0,0.55)",
};

const btnPrimary = {
  display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
  padding: "13px 20px",
  background: "linear-gradient(135deg, #ff3cac, #b44fff)",
  border: "none", borderRadius: 12, color: "#fff",
  fontSize: "0.95rem", fontWeight: 700,
  cursor: "pointer",
  boxShadow: "0 4px 20px rgba(180,79,255,0.35)",
  transition: "opacity 0.15s, transform 0.15s",
};

const btnGhost = {
  display: "flex", alignItems: "center", justifyContent: "center", gap: 7,
  padding: "11px 18px",
  background: "rgba(255,255,255,0.05)",
  border: "1px solid rgba(255,255,255,0.12)",
  borderRadius: 12, color: "rgba(255,255,255,0.7)",
  fontSize: "0.9rem", fontWeight: 600,
  cursor: "pointer",
  transition: "background 0.15s",
};

// ─── Small helpers ────────────────────────────────────────────────────────────

function AccessBadge({ hasAccess }) {
  return (
    <div style={{
      display: "inline-flex", alignItems: "center", gap: 8,
      padding: "8px 18px", borderRadius: 50, marginBottom: 24,
      fontSize: "0.85rem", fontWeight: 600,
      ...(hasAccess ? {
        background: "linear-gradient(135deg, rgba(0,245,255,0.12), rgba(180,79,255,0.12))",
        border: "1px solid rgba(0,245,255,0.35)",
        color: T.cyan,
      } : {
        background: "rgba(255,152,0,0.12)",
        border: "1px solid rgba(255,152,0,0.35)",
        color: "#ffb347",
      }),
    }}>
      {hasAccess ? <Check size={14} /> : <Eye size={14} />}
      {hasAccess ? "Verified — Physical possession confirmed" : "View Only — Tap NFC tag to interact"}
    </div>
  );
}

function KandiBead({ color, size = 48 }) {
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%", flexShrink: 0,
      background: `radial-gradient(circle at 35% 32%, rgba(255,255,255,0.55) 0%, ${color} 55%)`,
      border: "2px solid rgba(255,255,255,0.28)",
      boxShadow: `0 0 10px ${color}88, 0 2px 6px rgba(0,0,0,0.4)`,
    }} />
  );
}

function StatBubble({ value, label, color }) {
  return (
    <div style={{
      flex: 1, textAlign: "center", padding: "16px 12px",
      background: "rgba(255,255,255,0.04)",
      borderRadius: 16, border: `1px solid ${color}30`,
    }}>
      <div className="kk-readout" style={{
        fontSize: "clamp(1.7rem, 4vw, 2.3rem)", fontWeight: 700,
        color, lineHeight: 1, marginBottom: 5,
        textShadow: `0 0 20px ${color}80`,
      }}>
        {value}
      </div>
      <div style={{
        fontSize: "0.72rem", color: T.textMuted,
        textTransform: "uppercase", letterSpacing: "0.1em",
      }}>
        {label}
      </div>
    </div>
  );
}

// A strand of the app's signature bead motif standing in for a hairline rule —
// KK's version of a divider, not a generic <hr>.
const DIVIDER_BEAD_COLORS = [T.purple, T.pink, T.cyan, T.gold, T.pink, T.purple];

function BeadDivider() {
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 7,
      margin: "0 0 20px",
    }}>
      <div style={{ flex: 1, height: 1, background: "rgba(255,255,255,0.08)" }} />
      {DIVIDER_BEAD_COLORS.map((color, i) => (
        <KandiBead key={i} color={color} size={7} />
      ))}
      <div style={{ flex: 1, height: 1, background: "rgba(255,255,255,0.08)" }} />
    </div>
  );
}

function SectionLabel({ children }) {
  return (
    <p style={{
      fontSize: "0.75rem", fontWeight: 700, color: T.textMuted,
      textTransform: "uppercase", letterSpacing: "0.1em",
      margin: "0 0 10px",
    }}>
      {children}
    </p>
  );
}

function ErrorMsg({ msg }) {
  if (!msg) return null;
  return (
    <div style={{
      marginTop: 16, padding: "12px 16px",
      background: "rgba(255,60,60,0.1)",
      border: "1px solid rgba(255,60,60,0.3)",
      borderRadius: 10, color: "#ff8080",
      fontSize: "0.9rem",
    }}>
      {msg}
    </div>
  );
}

// ─── Claim modal — location + message for the new chapter ─────────────────────

function ClaimModal({ onConfirm, onCancel, loading }) {
  const [locationData, setLocationData] = useState({ location: "", lat: null, lng: null, eventName: null });
  const [message, setMessage]           = useState("");
  const inputStyle = {
    width: "100%", padding: "12px 16px",
    background: "rgba(255,255,255,0.07)",
    border: "1px solid rgba(255,255,255,0.15)",
    borderRadius: 10, color: T.textPrimary,
    fontSize: "0.95rem", outline: "none",
    boxSizing: "border-box", fontFamily: "inherit",
    marginBottom: 10,
  };
  return (
    <div style={{
      position: "fixed", inset: 0,
      background: "rgba(10,5,21,0.85)",
      backdropFilter: "blur(8px)",
      display: "flex", alignItems: "center", justifyContent: "center",
      zIndex: 9999, padding: 20,
    }}
      onClick={onCancel}
    >
      <div
        style={{
          background: "rgba(20,10,40,0.98)",
          border: "1px solid rgba(255,255,255,0.12)",
          borderRadius: 20, padding: "28px 24px",
          maxWidth: 400, width: "100%",
          boxShadow: "0 24px 64px rgba(0,0,0,0.7)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 style={{
          margin: "0 0 6px",
          fontFamily: "var(--font-display)",
          color: T.cyan,
          textShadow: `0 0 22px ${T.purple}70`,
          fontSize: "1.2rem", fontWeight: 700,
        }}>
          Claim This Kandi
        </h3>
        <p style={{ color: T.textMuted, fontSize: "0.88rem", margin: "0 0 16px" }}>
          It's yours now — where did you claim it?
        </p>

        <LocationPicker value={locationData} onChange={setLocationData} compact />

        <div style={{ position: "relative" }}>
          <MessageSquare size={14} color={T.textMuted} style={{
            position: "absolute", left: 14, top: "50%",
            transform: "translateY(-50%)",
          }} />
          <input
            placeholder="Message (optional)"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            style={{ ...inputStyle, paddingLeft: 36 }}
          />
        </div>

        <div style={{ display: "flex", gap: 10, marginTop: 6 }}>
          <button onClick={onCancel} style={{ ...btnGhost, flex: 1 }}>
            Cancel
          </button>
          <button
            onClick={() => onConfirm(locationData, message)}
            disabled={!locationData?.location?.trim() || loading}
            style={{ ...btnPrimary, flex: 2, opacity: (!locationData?.location?.trim() || loading) ? 0.6 : 1 }}
          >
            <Crown size={15} />
            {loading ? "Claiming…" : "Claim"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

const KandiProfile = ({ kandiId }) => {
  const [kandi, setKandi]                       = useState(null);
  const [loading, setLoading]                   = useState(true);
  const [actionLoading, setActionLoading]       = useState(false);
  const [error, setError]                       = useState("");
  const [uid, setUid]                           = useState(null);
  const [hasPhysicalAccess, setHasPhysicalAccess] = useState(false);
  const [copied, setCopied]                     = useState(null); // "full" | "view"
  const [showClaimModal, setShowClaimModal]     = useState(false);
  const [toast, setToast]                       = useState(null);

  const location = useLocation();
  const navigate = useNavigate();
  const API_URL  = process.env.REACT_APP_API_URL || "";
  const user     = JSON.parse(localStorage.getItem("user") || "{}");

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2800);
  };

  useEffect(() => {
    const urlParams  = new URLSearchParams(location.search);
    const uidFromUrl = urlParams.get("uid");
    if (uidFromUrl) {
      setUid(uidFromUrl);
      verifyAndLoadKandi(kandiId, uidFromUrl);
    } else {
      loadKandi(kandiId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  const verifyAndLoadKandi = async (id, uidToVerify) => {
    setLoading(true);
    setError("");
    try {
      const verifyRes = await axios.post(`${API_URL}/api/kandi/${id}/verify`, { uid: uidToVerify });
      if (verifyRes.data.verified) {
        setHasPhysicalAccess(true);
      } else {
        setError("Invalid UID — viewing in read-only mode");
        setHasPhysicalAccess(false);
      }
      await loadKandi(id);
    } catch (err) {
      setError("Invalid UID — viewing in read-only mode");
      setHasPhysicalAccess(false);
      await loadKandi(id);
    }
  };

  const loadKandi = async (id) => {
    try {
      // viewerId lets the backend decide whether the owner's profile is linkable
      const viewerParam = user?.id ? `?viewerId=${user.id}` : "";
      const res = await axios.get(`${API_URL}/api/kandi/${id}${viewerParam}`);
      setKandi(res.data);
    } catch (err) {
      setError("Failed to load kandi");
    } finally {
      setLoading(false);
    }
  };

  const openClaimModal = () => {
    if (!hasPhysicalAccess) {
      setError("You must scan the NFC tag with the full URL to claim this kandi");
      return;
    }
    if (!user.id) { setError("Please log in to claim this kandi"); return; }
    setError("");
    setShowClaimModal(true);
  };

  const handleClaim = async (locationData, message) => {
    setActionLoading(true);
    setError("");
    try {
      await axios.post(`${API_URL}/api/kandi/${kandiId}/claim`, {
        userId: user.id, username: user.username, uid,
        location: locationData?.location || "Unknown",
        lat: locationData?.lat ?? null,
        lng: locationData?.lng ?? null,
        message: message || "",
      });
      setShowClaimModal(false);
      showToast("Kandi claimed — it's yours!");
      await loadKandi(kandiId);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to claim kandi");
      setShowClaimModal(false);
    } finally {
      setActionLoading(false);
    }
  };

  const handleCopyUrl = () => {
    if (!kandiId || !uid) return;
    navigator.clipboard.writeText(`${window.location.origin}/kandi/${kandiId}?uid=${uid}`)
      .then(() => { setCopied("full"); setTimeout(() => setCopied(null), 2200); });
  };

  const handleCopyViewOnlyUrl = () => {
    if (!kandiId) return;
    navigator.clipboard.writeText(`${window.location.origin}/kandi/${kandiId}`)
      .then(() => { setCopied("view"); setTimeout(() => setCopied(null), 2200); });
  };

  // ── Loading ──────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{
        minHeight: "100vh", background: T.bg,
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <div style={{ textAlign: "center" }}>
          <div className="animate-neon-pulse" style={{
            width: 52, height: 52, borderRadius: "50%",
            margin: "0 auto 14px",
            background: `linear-gradient(135deg, ${T.purple}, ${T.cyan})`,
          }} />
          <p style={{ color: T.textMuted, fontSize: "0.9rem", margin: 0 }}>Loading kandi…</p>
        </div>
      </div>
    );
  }

  if (!kandi) {
    return (
      <div style={{
        minHeight: "100vh", background: T.bg,
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "80px 16px",
      }}>
        <div style={{ ...cardStyle, textAlign: "center" }}>
          <div style={{ fontSize: "3rem", marginBottom: 16 }}>🔍</div>
          <p style={{ color: T.textPrimary, fontWeight: 700, margin: "0 0 8px" }}>
            Kandi not found
          </p>
          <p style={{ color: T.textMuted, margin: "0 0 24px", fontSize: "0.9rem" }}>
            This kandi may not be registered yet
          </p>
          <button onClick={() => navigate("/scan")} style={{ ...btnPrimary }}>
            <Wifi size={15} /> Scan a Kandi
          </button>
        </div>
      </div>
    );
  }

  // ── Main render ──────────────────────────────────────────────────────────
  return (
    <div style={{
      minHeight: "100vh", background: T.bg,
      display: "flex", alignItems: "flex-start", justifyContent: "center",
      padding: "88px 16px 48px",
    }}>
      {/* Toast notification */}
      {toast && (
        <div style={{
          position: "fixed", top: 20, left: "50%", transform: "translateX(-50%)",
          background: toast.type === "success"
            ? "linear-gradient(135deg, rgba(0,245,255,0.2), rgba(180,79,255,0.2))"
            : "rgba(255,60,60,0.15)",
          border: toast.type === "success"
            ? "1px solid rgba(0,245,255,0.4)"
            : "1px solid rgba(255,60,60,0.4)",
          borderRadius: 50, padding: "10px 22px",
          color: "#fff", fontSize: "0.9rem", fontWeight: 600,
          zIndex: 10000, whiteSpace: "nowrap",
          boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
        }}>
          {toast.msg}
        </div>
      )}

      <div style={cardStyle}>
        <AccessBadge hasAccess={hasPhysicalAccess} />

        {/* Kandi header */}
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <div style={{
            fontSize: "clamp(3.5rem, 10vw, 5rem)", lineHeight: 1, marginBottom: 14,
            filter: "drop-shadow(0 0 20px rgba(255,255,255,0.15))",
          }}>
            {kandi.emoji || "🎨"}
          </div>
          <h1 style={{
            margin: "0 0 6px",
            fontFamily: "var(--font-display)",
            fontSize: "clamp(1.5rem, 4vw, 2rem)", fontWeight: 700,
            color: T.pink,
            textShadow: `0 0 24px ${T.purple}70`,
          }}>
            {kandi.name || "Unnamed Kandi"}
          </h1>
          <p style={{
            color: T.textMuted, fontSize: "0.82rem",
            fontFamily: "monospace", margin: 0, letterSpacing: "0.05em",
          }}>
            {kandi.id}
          </p>
        </div>

        {/* Stats */}
        <div style={{ display: "flex", gap: 12, marginBottom: 24 }}>
          <StatBubble value={kandi.scanCount ?? 0}        label="Scans"  color={T.cyan}   />
          <StatBubble value={kandi.history?.length ?? 0}  label="Owners" color={T.purple} />
        </div>

        {/* Bead colors */}
        {kandi.colors?.length > 0 && (
          <div style={{ marginBottom: 24 }}>
            <SectionLabel>Bead Colors</SectionLabel>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              {kandi.colors.map((color, i) => (
                <KandiBead key={i} color={color} size={48} />
              ))}
            </div>
          </div>
        )}

        <BeadDivider />

        {/* Ownership journey */}
        <div style={{ marginBottom: 24 }}>
          <SectionLabel>Journey</SectionLabel>
          {kandi.history?.length > 0 ? (
            <div>
              {kandi.history.map((entry, i) => (
                <div key={i} style={{
                  display: "flex", gap: 12, alignItems: "flex-start",
                  padding: "12px 0",
                  borderBottom: i < kandi.history.length - 1
                    ? "1px solid rgba(255,255,255,0.05)" : "none",
                }}>
                  <div style={{
                    width: 34, height: 34, borderRadius: "50%", flexShrink: 0,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    background: i === 0
                      ? `linear-gradient(135deg, ${T.gold}, ${T.pink})`
                      : "rgba(255,255,255,0.07)",
                    border: i === 0 ? "none" : "1px solid rgba(255,255,255,0.1)",
                  }}>
                    {i === 0
                      ? <Crown size={15} color="#fff" />
                      : <span style={{ fontSize: "0.75rem", color: T.textMuted, fontWeight: 700 }}>{i + 1}</span>
                    }
                  </div>
                  <div>
                    <p style={{ margin: 0, fontSize: "0.95rem" }}>
                      <UserLink
                        username={entry.username || entry.user || "Unknown"}
                        userId={entry.userId}
                        style={{ fontSize: "0.95rem" }}
                      />
                    </p>
                    <p style={{ margin: "2px 0 0", color: T.textMuted, fontSize: "0.8rem" }}>
                      {new Date(entry.timestamp || entry.date || Date.now()).toLocaleDateString("en-US", {
                        month: "short", day: "numeric", year: "numeric",
                      })}
                      {entry.location && ` · ${entry.location}`}
                    </p>
                    {entry.message && (
                      <p style={{ margin: "3px 0 0", color: T.textMuted, fontSize: "0.8rem", fontStyle: "italic" }}>
                        "{entry.message}"
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p style={{ color: T.textMuted, fontSize: "0.9rem", margin: 0 }}>
              No history yet — be the first owner!
            </p>
          )}
        </div>

        <BeadDivider />

        {/* Actions */}
        {hasPhysicalAccess ? (
          <>
            <div style={{ display: "flex", gap: 12, marginBottom: 14 }}>
              <button
                onClick={openClaimModal}
                disabled={actionLoading}
                style={{ ...btnPrimary, flex: 1, opacity: actionLoading ? 0.6 : 1 }}
              >
                <Crown size={16} />
                {actionLoading ? "Working…" : "Claim"}
              </button>
            </div>

            <div style={{ marginBottom: 20 }}>
              <SectionLabel>Share</SectionLabel>
              <div style={{ display: "flex", gap: 10 }}>
                <button onClick={handleCopyUrl} style={{ ...btnGhost, flex: 1 }}>
                  {copied === "full" ? <Check size={14} color={T.cyan} /> : <Copy size={14} />}
                  {copied === "full" ? "Copied!" : "Full URL"}
                </button>
                <button onClick={handleCopyViewOnlyUrl} style={{ ...btnGhost, flex: 1 }}>
                  {copied === "view" ? <Check size={14} color={T.cyan} /> : <Eye size={14} />}
                  {copied === "view" ? "Copied!" : "View-Only"}
                </button>
              </div>
            </div>
          </>
        ) : (
          <div style={{
            textAlign: "center", padding: "18px 20px", marginBottom: 20,
            background: "rgba(255,152,0,0.07)",
            borderRadius: 12, border: "1px solid rgba(255,152,0,0.2)",
          }}>
            <p style={{ color: "#ffb347", fontWeight: 600, margin: "0 0 6px", fontSize: "0.95rem" }}>
              Tap the NFC tag to interact
            </p>
            <p style={{ color: T.textMuted, fontSize: "0.85rem", margin: 0 }}>
              Your phone will open the full URL with claim access
            </p>
          </div>
        )}

        <ErrorMsg msg={error} />

        <button
          onClick={() => navigate("/dashboard")}
          style={{ ...btnGhost, width: "100%", marginTop: 8 }}
        >
          <ArrowLeft size={15} />
          Back to Dashboard
        </button>
      </div>

      {/* Claim modal */}
      {showClaimModal && (
        <ClaimModal
          loading={actionLoading}
          onConfirm={handleClaim}
          onCancel={() => setShowClaimModal(false)}
        />
      )}
    </div>
  );
};

export default KandiProfile;
